import palette from 'theme/palette'

// Base font size for the table in mobile WebView mode.
// Matched to the sibling "ข้อมูลบ่อ" daily-summary table
// (TableSummaryDashboard), which renders correctly in the same Flutter WebView:
// it uses 1.625rem (26px) on every mobile cell and relies on the zoom wrapper
// (initialScale 0.5) to scale it down to a legible ~13px. Earlier we had dropped
// this to 1.125rem (18px) to stop pinned/badge labels clipping, but that left
// the Lab table inconsistent with the reference; instead we adopt the reference
// font and widen the pinned columns below so full Thai words ("บ่อพักใหญ่",
// "กระเพาะ", badge labels เหลือง/เขียว/ดำ) fit without clipping.
export const MOBILE_FONT_SIZE = '1.625rem'

// Pinned (sticky) column widths. Kept as named constants so the sticky `left`
// offsets, the AGE-row spanning cell and the data-cell width stay in sync.
// Sized to fit the longest Thai labels at the 26px reference font (matching
// TableSummaryDashboard, whose pinned columns are 300/180px): "บ่อพักใหญ่"
// (col1), "กระเพาะ" (col2), "อายุกุ้ง" spanning col2+col3, badge labels
// เหลือง/เขียว/ดำ (col3). The zoom wrapper shrinks the whole pinned region, so
// wider tracks here still leave room for the date matrix on screen.
export const PINNED_COL1_WIDTH = 200
export const PINNED_COL2_WIDTH = 150
export const PINNED_COL3_WIDTH = 140
export const DATA_COL_WIDTH = 150

// Shared overflow guard for pinned-column text. Text is start-aligned (not
// centred) so that if a label is still wider than its column it is truncated
// with a single trailing ellipsis instead of being chopped on both ends.
const pinnedTextClip = {
  overflow: 'hidden' as const,
  whiteSpace: 'nowrap' as const,
  textOverflow: 'ellipsis' as const,
}

export const tableStyles = {
  container: (isMobile: boolean, isIOS: boolean = false) => ({
    overflowX: 'auto' as const,
    overflowY: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    border: `1.5px solid ${palette.light.grey[300]}`,
    position: 'relative' as const,
    margin: '0',
    maxWidth: '100%',
    height: '100%',
    fontSize: isMobile ? MOBILE_FONT_SIZE : undefined,
    fontWeight: isMobile ? 700 : undefined,
    touchAction: 'pan-x pan-y' as const,
    WebkitTouchCallout: 'none' as const,
    userSelect: 'none' as const,
    ...(isIOS
      ? {
          overscrollBehavior: 'none' as const,
          overscrollBehaviorX: 'none' as const,
          overscrollBehaviorY: 'none' as const,
        }
      : {}),
  }),
  tableWrapper: {
    position: 'relative' as const,
    minWidth: 'max-content',
    width: 'fit-content',
    touchAction: 'pan-x pan-y' as const,
  },
  headerRow: {
    display: 'flex' as const,
    position: 'sticky' as const,
    top: 0,
    backgroundColor: palette.light.grey[200],
    zIndex: 10,
    borderBottom: `1.5px solid ${palette.light.grey[300]}`,
    minWidth: 'max-content',
    color: palette.light.grey[600],
    touchAction: 'pan-x pan-y' as const,
    pointerEvents: 'auto' as const,
  },
  row: {
    display: 'flex' as const,
    minWidth: 'max-content',
    willChange: 'transform' as const,
    height: '40px',
    touchAction: 'pan-x pan-y',
  },
  stickyCol1: {
    position: 'sticky' as const,
    left: 0,
    backgroundColor: palette.light.grey[200],
    zIndex: 5,
    width: `${PINNED_COL1_WIDTH}px`,
    minWidth: `${PINNED_COL1_WIDTH}px`,
    flexBasis: `${PINNED_COL1_WIDTH}px`,
    height: '40px',
    flexShrink: 0,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
    padding: '0 6px',
    touchAction: 'pan-x pan-y' as const,
    pointerEvents: 'auto' as const,
    ...pinnedTextClip,
  },
  stickyCol2: {
    position: 'sticky' as const,
    left: `${PINNED_COL1_WIDTH}px`,
    backgroundColor: palette.light.grey[200],
    zIndex: 5,
    width: `${PINNED_COL2_WIDTH}px`,
    minWidth: `${PINNED_COL2_WIDTH}px`,
    flexBasis: `${PINNED_COL2_WIDTH}px`,
    height: '40px',
    flexShrink: 0,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
    padding: '0 6px',
    touchAction: 'pan-x pan-y' as const,
    pointerEvents: 'auto' as const,
    ...pinnedTextClip,
  },
  stickyCol3: {
    position: 'sticky' as const,
    left: `${PINNED_COL1_WIDTH + PINNED_COL2_WIDTH}px`,
    backgroundColor: palette.light.grey[200],
    zIndex: 5,
    width: `${PINNED_COL3_WIDTH}px`,
    minWidth: `${PINNED_COL3_WIDTH}px`,
    flexBasis: `${PINNED_COL3_WIDTH}px`,
    height: '40px',
    flexShrink: 0,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
    padding: '0 6px',
    touchAction: 'pan-x pan-y' as const,
    pointerEvents: 'auto' as const,
    ...pinnedTextClip,
  },
  cell: {
    padding: '0',
    textAlign: 'center' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: `${DATA_COL_WIDTH}px`,
    minWidth: `${DATA_COL_WIDTH}px`,
    flexBasis: `${DATA_COL_WIDTH}px`,
    height: '40px',
    flexShrink: 0,
    touchAction: 'pan-x pan-y',
  },
  groupCell: {
    padding: '0 6px',
    textAlign: 'start' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
    height: '40px',
    flexShrink: 0,
    backgroundColor: palette.light.grey[200],
    touchAction: 'pan-x pan-y' as const,
    pointerEvents: 'auto' as const,
    ...pinnedTextClip,
  },
  headerCell: {
    boxSizing: 'border-box' as const,
    borderRight: `1.5px solid ${palette.light.grey[300]}`,
    padding: '10px 8px',
    textAlign: 'center' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: `${DATA_COL_WIDTH}px`,
    minWidth: `${DATA_COL_WIDTH}px`,
    flexBasis: `${DATA_COL_WIDTH}px`,
    height: '40px',
    flexShrink: 0,
    overflow: 'hidden' as const,
    whiteSpace: 'nowrap' as const,
    textOverflow: 'ellipsis' as const,
    touchAction: 'pan-x pan-y' as const,
    pointerEvents: 'auto' as const,
  },
}
